/* DIREVULF — Shop by Fandom.
 *
 * Two jobs:
 *   1. The "Did you know" strip: a seamless marquee whose speed is a real
 *      pixels-per-second number, measured from the content so the loop point
 *      is invisible and the pace is the same however many items there are.
 *   2. Background videos: the theme's markup holds its sources in data-src so
 *      a video is never render-blocking. Nothing was attaching them, so every
 *      background video on the site sat on its poster. This attaches them
 *      when they scroll into view.
 *
 * No network calls. Nothing here talks to anything outside this store.
 */
(function () {
  'use strict';

  var MOBILE = 749;

  function all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function saveData() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    return c.effectiveType === 'slow-2g' || c.effectiveType === '2g';
  }

  /* ---------------------------------------------------------------- marquee */

  function marquee(root) {
    var track = root.querySelector('[data-dv-dyk-track]');
    if (!track) return;

    var sets = all('[data-dv-dyk-set]', track);
    if (!sets.length) return;

    var original = sets[0];
    var extras = [];

    if (root.getAttribute('data-pause-hover') === 'true') root.classList.add('dv-dyk--pausehover');
    if (root.getAttribute('data-pause-focus') === 'true') root.classList.add('dv-dyk--pausefocus');

    function speed() {
      var mobile = window.innerWidth <= MOBILE;
      var raw = root.getAttribute(mobile ? 'data-speed-mobile' : 'data-speed');
      var n = parseFloat(raw);
      return n > 0 ? n : 45;
    }

    function clearExtras() {
      extras.forEach(function (el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      extras = [];
    }

    function measure() {
      if (reducedMotion()) {
        root.classList.add('dv-dyk--static');
        track.style.removeProperty('--dv-dyk-shift');
        track.style.removeProperty('--dv-dyk-dur');
        return;
      }
      root.classList.remove('dv-dyk--static');

      clearExtras();

      var width = original.getBoundingClientRect().width;
      if (!width) return;

      // One set has to be at least as wide as the viewport, otherwise the
      // second copy runs out before it reaches the left edge and the loop
      // shows a gap. Duplicate the set until it covers the strip.
      var viewport = root.querySelector('.dv-dyk__viewport');
      var need = viewport ? viewport.getBoundingClientRect().width : 0;
      var copies = 1;
      while (width * copies < need && copies < 12) copies += 1;

      if (copies > 1) {
        // Grow the first set so its own width covers the viewport, then make
        // the trailing copy match it.
        var addition = document.createDocumentFragment();
        for (var i = 1; i < copies; i++) {
          Array.prototype.forEach.call(original.children, function (li) {
            var clone = li.cloneNode(true);
            clone.setAttribute('data-dv-dyk-extra', '');
            clone.setAttribute('aria-hidden', 'true');
            var link = clone.querySelector('a');
            if (link) link.setAttribute('tabindex', '-1');
            addition.appendChild(clone);
          });
        }
        original.appendChild(addition);
        extras = all('[data-dv-dyk-extra]', original);
        width = original.getBoundingClientRect().width;
      }

      // Rebuild the trailing copy so both halves are identical.
      var tail = sets[1];
      if (tail) {
        tail.innerHTML = '';
        Array.prototype.forEach.call(original.children, function (li) {
          var clone = li.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          var link = clone.querySelector('a');
          if (link) {
            var span = document.createElement('span');
            span.className = 'dv-dyk__text';
            span.textContent = link.textContent;
            link.parentNode.replaceChild(span, link);
          }
          tail.appendChild(clone);
        });
      }

      track.style.setProperty('--dv-dyk-shift', '-' + Math.round(width) + 'px');
      track.style.setProperty('--dv-dyk-dur', (width / speed()).toFixed(2) + 's');
    }

    var pending = null;
    function schedule() {
      if (pending) clearTimeout(pending);
      pending = setTimeout(measure, 150);
    }

    measure();
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);

    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(measure).catch(function () {});
    }

    if (window.matchMedia) {
      var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (mq.addEventListener) mq.addEventListener('change', measure);
      else if (mq.addListener) mq.addListener(measure);
    }
  }

  /* ----------------------------------------------------------- hero videos */

  function attach(box) {
    if (box.getAttribute('data-dv-video-ready') === 'true') return;
    box.setAttribute('data-dv-video-ready', 'true');

    var video = box.querySelector('video');
    if (!video) return;

    var sources = all('source[data-src]', video);
    if (!sources.length) return;

    sources.forEach(function (source) {
      source.setAttribute('src', source.getAttribute('data-src'));
      source.removeAttribute('data-src');
    });

    video.load();

    var wantsAutoplay = box.getAttribute('data-autoplay') !== 'false';
    var respects = box.getAttribute('data-respect-motion') !== 'false';

    if (!wantsAutoplay) return;
    if (respects && reducedMotion()) return;
    if (saveData()) return;

    var started = video.play();
    if (started && typeof started.catch === 'function') {
      // Autoplay refused (a browser policy, a low-power mode). The poster
      // stays visible; that is the honest fallback, not an error.
      started.catch(function () {
        box.setAttribute('data-dv-video-blocked', 'true');
      });
    }
  }

  function videos() {
    var boxes = all('[data-dv-hero-video]');
    if (!boxes.length) return;

    if (!('IntersectionObserver' in window)) {
      boxes.forEach(attach);
      return;
    }

    var seen = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        seen.unobserve(entry.target);
        attach(entry.target);
      });
    }, { rootMargin: '200px 0px' });

    boxes.forEach(function (box) {
      seen.observe(box);
    });
  }

  /* -------------------------------------------------------------- start up */

  function start() {
    all('[data-dv-dyk]').forEach(marquee);
    videos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (event) {
    all('[data-dv-dyk]', event.target).forEach(marquee);
    videos();
  });
  document.addEventListener('shopify:section:unload', function () {});
})();